import { isAxiosError } from 'axios'
import { platformClient } from '@/api/http/platformClient'
import { ApiError, type ApiResponse, type PaginationMeta } from '@/api/http/types'
import type {
  InviteTeamMemberBody,
  ListTeamMembersParams,
  TeamMemberDto,
} from './teamMembers.types'

function appendTeamFilters(
  sp: URLSearchParams,
  params: Pick<ListTeamMembersParams, 'role' | 'status' | 'search' | 'sortBy' | 'sortOrder'>,
) {
  if (params.role) sp.set('role', params.role)
  if (params.status) sp.set('status', params.status)
  if (params.search?.trim()) sp.set('search', params.search.trim())
  sp.set('sortBy', params.sortBy ?? 'joinDate')
  sp.set('sortOrder', params.sortOrder ?? 'desc')
}

function buildQueryString(params: ListTeamMembersParams): string {
  const sp = new URLSearchParams()
  sp.set('page', String(params.page))
  sp.set('pageSize', String(params.pageSize))
  appendTeamFilters(sp, params)
  return sp.toString()
}

function buildExportQueryString(
  params: Omit<ListTeamMembersParams, 'page' | 'pageSize'>,
): string {
  const sp = new URLSearchParams()
  appendTeamFilters(sp, params)
  return sp.toString()
}

async function throwIfBlobIsApiError(blob: Blob, status: number): Promise<never> {
  const text = await blob.text()
  try {
    const body = JSON.parse(text) as ApiResponse
    if (body?.error) {
      throw new ApiError(
        body.message,
        status,
        body.error.code,
        body.error.details,
      )
    }
  } catch (e) {
    if (e instanceof ApiError) throw e
  }
  throw new ApiError(text || 'Request failed', status, 'unknown_error', null)
}

export async function downloadTeamMembersCsv(
  params: Omit<ListTeamMembersParams, 'page' | 'pageSize'>,
): Promise<{ blob: Blob; filename: string }> {
  const qs = buildExportQueryString(params)
  try {
    const res = await platformClient.get<Blob>(`/api/v1/team/export?${qs}`, {
      responseType: 'blob',
    })
    const data = res.data
    if (data.type === 'application/json' || data.type === 'text/json') {
      await throwIfBlobIsApiError(data, res.status)
    }
    const cd = res.headers['content-disposition'] as string | undefined
    let filename = 'team-members.csv'
    if (cd) {
      const m = /filename\*?=(?:UTF-8'')?["']?([^"'\s;]+)/i.exec(cd)
      if (m?.[1]) filename = decodeURIComponent(m[1].replaceAll(/['"]/g, ''))
    }
    return { blob: data, filename }
  } catch (e) {
    if (isAxiosError(e) && e.response?.data instanceof Blob) {
      await throwIfBlobIsApiError(e.response.data, e.response.status)
    }
    throw e
  }
}

export function triggerCsvDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function fetchTeamMembers(
  params: ListTeamMembersParams,
  signal?: AbortSignal,
): Promise<{
  items: TeamMemberDto[]
  meta: PaginationMeta | null
}> {
  const qs = buildQueryString(params)
  const { data } = await platformClient.get<ApiResponse<TeamMemberDto[]>>(
    `/api/v1/team?${qs}`,
    { signal },
  )
  return { items: data.data, meta: data.meta }
}

export async function fetchTeamMemberById(memberId: string): Promise<TeamMemberDto> {
  const { data } = await platformClient.get<ApiResponse<TeamMemberDto>>(
    `/api/v1/team/${encodeURIComponent(memberId)}`,
  )
  return data.data
}

export async function inviteTeamMember(
  body: InviteTeamMemberBody,
): Promise<TeamMemberDto> {
  const { data } = await platformClient.post<ApiResponse<TeamMemberDto>>(
    '/api/v1/team/invite',
    body,
  )
  return data.data
}

export async function deactivateTeamMember(memberId: string): Promise<void> {
  await platformClient.post<ApiResponse<unknown>>(
    `/api/v1/team/${encodeURIComponent(memberId)}/deactivate`,
  )
}

export async function reactivateTeamMember(memberId: string): Promise<void> {
  await platformClient.post<ApiResponse<unknown>>(
    `/api/v1/team/${encodeURIComponent(memberId)}/reactivate`,
  )
}

export async function resendTeamInvitation(memberId: string): Promise<void> {
  await platformClient.post<ApiResponse<unknown>>(
    `/api/v1/team/${encodeURIComponent(memberId)}/resend-invitation`,
  )
}
